import { useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { LifeBuoy, AlertTriangle, Loader2, Send } from "lucide-react";

const CATEGORY_HINTS = {
  booking: "Booking not confirmed / modification / cancellation issue",
  payment: "Payment failed, amount deducted, invoice missing, refund not received",
  hotel: "Hotel not responding, wrong room, overbooking, refused check-in",
  technical: "App crash, OTP not received, login stuck, page broken",
  other: "Anything else — feedback, suggestion, complaint",
};

/**
 * NewTicket — form to raise a support ticket.
 * Priority defaults to 'normal'; 'high' is unlocked when the user checks the
 * emergency box (safety, fraud, denied check-in, double booking).
 */
export default function NewTicket() {
  const navigate = useNavigate();
  const [category, setCategory] = useState("booking");
  const [subject, setSubject] = useState("");
  const [description, setDescription] = useState("");
  const [priority, setPriority] = useState("normal");
  const [isEmergency, setIsEmergency] = useState(false);
  const [bookingId, setBookingId] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    if (subject.trim().length < 4) return toast.error("Subject is too short (min 4 chars).");
    if (description.trim().length < 10) return toast.error("Please describe the issue in more detail (min 10 chars).");
    setSubmitting(true);
    try {
      const { data } = await api.post("/support/tickets", {
        category, subject, description, priority: isEmergency ? "high" : priority,
        is_emergency: isEmergency,
        booking_id: bookingId.trim() || undefined,
      });
      toast.success("Ticket raised — our team will respond within 24 hrs.");
      navigate(`/support/ticket/${data.ticket.ticket_id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create ticket");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] bg-slate-50 py-10">
      <div className="max-w-2xl mx-auto px-4 md:px-8">
        <div className="hw-label text-blue-700">Support · New ticket</div>
        <h1 className="hw-head text-3xl md:text-4xl font-black text-slate-900 mt-2 flex items-center gap-2" data-testid="new-ticket-heading">
          <LifeBuoy className="w-8 h-8 text-emerald-600" /> Raise a ticket
        </h1>
        <p className="text-slate-600 mt-2 leading-relaxed">
          For payment, refund, dispute or technical issues. Room-level questions? <a href="/support" className="text-blue-700 hover:underline">Chat with your hotel</a> for faster help.
        </p>

        <div className="hw-card p-6 md:p-8 mt-6 space-y-4" data-testid="new-ticket-form">
          <div>
            <Label className="hw-label">Category *</Label>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="rounded-sm mt-1 w-full h-11 border border-slate-200 px-3 text-sm bg-white"
              data-testid="ticket-category"
            >
              {Object.keys(CATEGORY_HINTS).map((k) => (
                <option key={k} value={k}>{k.charAt(0).toUpperCase() + k.slice(1)}</option>
              ))}
            </select>
            <p className="text-xs text-slate-500 mt-1">{CATEGORY_HINTS[category]}</p>
          </div>

          <div>
            <Label className="hw-label">Subject *</Label>
            <Input value={subject} onChange={(e) => setSubject(e.target.value)}
              className="rounded-sm mt-1" placeholder="Short title of the issue"
              maxLength={200} data-testid="ticket-subject" />
          </div>

          <div>
            <Label className="hw-label">Booking ID (optional)</Label>
            <Input value={bookingId} onChange={(e) => setBookingId(e.target.value)}
              className="rounded-sm mt-1 hw-mono" placeholder="bkg_xxx (paste from your bookings)"
              data-testid="ticket-booking-id" />
          </div>

          <div>
            <Label className="hw-label">Describe the issue *</Label>
            <Textarea rows={6} value={description} onChange={(e) => setDescription(e.target.value)}
              className="rounded-sm mt-1" placeholder="What happened? When did it start? What did you try? The more details, the faster we can help."
              data-testid="ticket-description" />
          </div>

          <div>
            <Label className="hw-label">Priority</Label>
            <div className="flex gap-2 mt-1">
              {["low", "normal", "high"].map((p) => (
                <button key={p} type="button"
                  disabled={isEmergency}
                  onClick={() => setPriority(p)}
                  data-testid={`ticket-priority-${p}`}
                  className={`flex-1 rounded-sm border py-2 text-sm font-bold capitalize transition-colors disabled:opacity-50 ${
                    (isEmergency ? "high" : priority) === p
                      ? "border-blue-700 bg-blue-50 text-blue-700"
                      : "border-slate-300 text-slate-700 hover:border-slate-500"
                  }`}>{p}</button>
              ))}
            </div>
          </div>

          <label className="flex items-start gap-3 hw-card border-red-200 bg-red-50 p-3 cursor-pointer" data-testid="ticket-emergency-row">
            <input type="checkbox" checked={isEmergency} onChange={(e) => setIsEmergency(e.target.checked)}
              className="mt-1 h-4 w-4 accent-red-600" data-testid="ticket-emergency-checkbox" />
            <span className="text-sm text-slate-800">
              <span className="hw-head font-black text-red-800 inline-flex items-center gap-1"><AlertTriangle className="w-4 h-4" /> This is an emergency</span><br />
              Denied check-in despite confirmed booking, safety issue, suspected fraud, hotel closed / double booking. Emergency tickets auto-set to HIGH priority and are shown first to our team.
            </span>
          </label>

          <Button onClick={submit} disabled={submitting}
            className="w-full bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm font-bold py-6 h-auto"
            data-testid="ticket-submit-btn">
            {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
            {submitting ? "Sending…" : "Send ticket to SwaanStay support"}
          </Button>
        </div>
      </div>
    </div>
  );
}
